/**
 * HoverPreview — the lightweight floating card that appears on graph node
 * hover (doc 147 §5.3 redesign · task #11). Pure presentational; the parent
 * tracks debounce + positioning. Returns null when no entity is hovered, so
 * conditionally including it in the tree is just `<HoverPreview ... />`.
 */
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';
import { entityById, domainById, entityDegree } from '../state/selectors.js';

export interface HoverPreviewProps {
  manifest: AtlasManifest;
  entityId: string | undefined;
  x: number;
  y: number;
}

/**
 * Conservative tooltip dimensions used to clamp position before viewport
 * measurement (CSS `max-width: 240px`, plus padding/border for the height
 * estimate). A few extra pixels of margin keep the tooltip away from the
 * viewport gutter.
 */
const TOOLTIP_MAX_W = 256;
const TOOLTIP_MAX_H = 80;
const VIEWPORT_MARGIN = 8;

export function HoverPreview({
  manifest,
  entityId,
  x,
  y,
}: HoverPreviewProps): React.JSX.Element | null {
  if (!entityId) return null;
  const entity = entityById(manifest, entityId);
  if (!entity) return null;
  const domain = domainById(manifest, entity.domain);
  const { total } = entityDegree(manifest, entityId);
  const refLabel = total === 1 ? '1 ref' : `${total} refs`;
  const { left, top } = clampToViewport(x, y);
  return (
    <div
      className="hover-preview"
      role="tooltip"
      aria-label={`${entity.name} — preview`}
      style={{ left: `${left}px`, top: `${top}px` }}
    >
      <div className="hover-preview-name">{entity.name}</div>
      <div className="hover-preview-meta">
        {domain && (
          <span style={{ display: 'inline-flex', gap: '4px', marginRight: '8px' }}>
            <DomainBadge domainId={entity.domain} size="sm" />
            <LayerBadge layer={(domain.layer ?? 'L5') as DomainLayer} size="sm" />
          </span>
        )}
        {entity.fields.length} fields · {refLabel}
      </div>
    </div>
  );
}

function clampToViewport(x: number, y: number): { left: number; top: number } {
  const vw = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth;
  const vh = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerHeight;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(x, vw - TOOLTIP_MAX_W - VIEWPORT_MARGIN),
  );
  const top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(y, vh - TOOLTIP_MAX_H - VIEWPORT_MARGIN),
  );
  return { left, top };
}
